/**
 * LessonLockedNotice — shown in place of a lesson's content blocks when the
 * backend gate (ADR-0019) withholds the body (`locked: true`). Surfaces the
 * required tier and routes to the upgrade flow (/checkout?tier=…), reusing the
 * paywall tier-name i18n keys.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";

/** Backend EntitlementTier serial name → the paywall tier-name i18n key. */
const TIER_NAME_KEY: Record<string, string> = {
  class_c: "paywall.tiers.classC.name",
  cdl: "paywall.tiers.cdl.name",
  tourist_in: "paywall.tiers.tourist.name",
  tourist_out: "paywall.tiers.outbound.name",
};

export interface LessonLockedNoticeProps {
  requiresUpgradeTo?: string | null;
}

export default function LessonLockedNotice({
  requiresUpgradeTo,
}: LessonLockedNoticeProps): JSX.Element {
  const { t } = useTranslation();
  const tierKey = requiresUpgradeTo ? TIER_NAME_KEY[requiresUpgradeTo] : undefined;
  const tierName = tierKey ? t(tierKey) : (requiresUpgradeTo ?? "");
  const checkoutHref = requiresUpgradeTo
    ? `/checkout?tier=${encodeURIComponent(requiresUpgradeTo)}`
    : "/checkout";

  return (
    <section
      role="region"
      aria-label={t("lesson.locked.heading")}
      className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center"
    >
      <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
        <Lock aria-hidden="true" size={22} className="text-amber-600" />
      </span>
      <h2 className="font-display text-lg font-bold text-amber-900">
        {t("lesson.locked.heading")}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-amber-800">
        {t("lesson.locked.body", { tier: tierName })}
      </p>
      <Link
        to={checkoutHref}
        className="mt-5 inline-block rounded-full bg-brand-primary px-6 py-2.5 text-sm font-semibold text-white no-underline transition-all hover:bg-brand-primary/90"
      >
        {t("lesson.locked.cta")}
      </Link>
    </section>
  );
}
